import { BrandKitStepProps } from 'components/FirstTimeExperienceModal/Types'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import React, { CSSProperties, useState } from 'react'
import Styles from './BrandKitImages.module.scss'
import { Button } from 'blaze-ui/Button'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { UploadButton } from './UploadButton'
import { EmptyState } from './EmptyState'
import { Text } from 'blaze-ui/Text/Text'
import { AlmanacFile, BrandKit } from 'types'
import { IMediaFile } from 'components/Design/UploadsTab/Types'
import { ChevronDown, ChevronUp } from 'blaze-ui/icons'
import { MediaFileClient } from 'api/clients/MediaFileClient'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { reportError } from 'utils/error'
import { ImageGridItem } from './ImageGridItem'

interface BrandKitImagesContentProps {
  brandKit: BrandKit
  imagesDescription?: string
  imageSize?: number
  onImagesChange?: (brandKit: BrandKit) => void
}

export const BrandKitImagesContent = ({
  brandKit,
  imagesDescription,
  imageSize,
  onImagesChange,
}: BrandKitImagesContentProps) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const { isMobile } = useResponsiveDesign()
  const [images, setImages] = useState<IMediaFile[]>(brandKit.media_files)
  const [showAllImages, setShowAllImages] = useState(false)

  const imagesPerView = isMobile ? 7 : 9
  const displayImages = showAllImages ? images : images.slice(0, imagesPerView)
  const hasMoreImages = images.length > imagesPerView

  const onUploadSuccess = (files?: AlmanacFile[]) => {
    const newImages =
      files?.map((file) => ({
        id: file.id,
        source_url: file.source_url || '',
        mime_type: file.mime_type || '',
        variants: file.variants || [],
        asset_id: file.media_file_id,
      })) || []

    queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits'])
    queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits', 'detail', brandKit.id])
    setImages((prevImages) => [...newImages, ...prevImages])
    onImagesChange?.({ ...brandKit, media_files: [...newImages, ...brandKit.media_files] })
  }

  const handleDeleteImage = async (id?: number) => {
    if (!id) return

    // Keep a copy of the current images in case of an error
    const currentImages = images

    // Optimistically update the state
    setImages((prevImages) => {
      const newImages = prevImages.filter((image) => image.asset_id !== id)
      onImagesChange?.({ ...brandKit, media_files: newImages })
      return newImages
    })

    await MediaFileClient.destroy(workspaceId, id)
      .then(() => {
        queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits', 'detail', brandKit.id])
      })
      .catch((error) => {
        setImages(currentImages)
        onImagesChange?.({ ...brandKit, media_files: currentImages })
        reportError(error)
        showErrorMessageToast('There was an error deleting the image')
      })
  }

  return (
    <>
      {imagesDescription && (
        <div className={Styles.imagesDescription}>
          <Text color="var(--dark-60)">{imagesDescription}</Text>
        </div>
      )}
      {images.length === 0 && <EmptyState brandKitId={brandKit.id} onSuccess={onUploadSuccess} />}
      <div
        className={Styles.imageGrid}
        style={
          {
            '--image-size': imageSize ? `${imageSize}px` : '1fr',
          } as CSSProperties
        }
      >
        {images.length > 0 && (
          <UploadButton brandKitId={brandKit.id} onSuccess={onUploadSuccess} className={Styles.uploadButtonContainer} />
        )}
        {displayImages.map((image) => {
          return <ImageGridItem key={image.id} image={image} onDelete={handleDeleteImage} />
        })}
      </div>

      {!showAllImages && hasMoreImages && (
        <div className={Styles.seeLessContainer}>
          <Button variant="tertiary" onPress={() => setShowAllImages(true)} size="lg" frontIcon={ChevronDown}>
            See {images.length - imagesPerView} More
          </Button>
        </div>
      )}

      <ResponsiveDesign.Mobile>
        {showAllImages && images.length > imagesPerView && (
          <div className={Styles.seeLessContainer}>
            <Button variant="tertiary" onPress={() => setShowAllImages(false)} size="lg" fullWidth>
              See Less
            </Button>
          </div>
        )}
      </ResponsiveDesign.Mobile>

      <ResponsiveDesign.Desktop>
        {showAllImages && images.length > imagesPerView && (
          <div className={Styles.seeLessContainer}>
            <Button variant="tertiary" onPress={() => setShowAllImages(false)} size="lg" frontIcon={ChevronUp}>
              See Less
            </Button>
          </div>
        )}
      </ResponsiveDesign.Desktop>
    </>
  )
}

export const BrandKitImages: React.FC<
  BrandKitStepProps & { title?: string; onImagesChange?: (brandKit: BrandKit) => void }
> = ({ onNext, onBack, onSkip, brandKit, title, withinOnboarding = false, onImagesChange }) => {
  const imagesDescription = withinOnboarding
    ? "Add more media now or later. We'll use it to quickly generate social posts, videos, blogs, and more."
    : 'We gave you a head start with some of the images we found on your website.'
  const description =
    brandKit.media_files.length > 0
      ? imagesDescription
      : 'Upload images or videos so we can generate content from them later'

  return (
    <MultiStepStepLayout
      title={title}
      description={withinOnboarding ? description : undefined}
      onBack={onBack}
      onNext={onNext}
      onSkip={onSkip}
      nextButtonText="Review Brand Voice"
    >
      <BrandKitImagesContent
        brandKit={brandKit}
        onImagesChange={onImagesChange}
        imagesDescription={withinOnboarding ? undefined : imagesDescription}
      />
    </MultiStepStepLayout>
  )
}
